// cartApi.ts
import { createApi } from "@reduxjs/toolkit/query/react";
import type * as cartTypes from "./cart.types";
import { axiosBaseQuery } from "./helpers/base-query";
import { cartApiInstance } from "./axios/api-instances";

const baseQuery = axiosBaseQuery(cartApiInstance);

export const cartApi = createApi({
  reducerPath: "cartApi",
  baseQuery,
  tagTypes: ["CartSession"],
  endpoints: (builder) => ({
    listZones: builder.query<
      cartTypes.MultiResultsResponse<cartTypes.ZoneResponse>,
      void
    >({
      query: () => ({
        url: "/zones?active=1",
        method: "GET",
      }),
    }),

    productByBarcode: builder.query<
      cartTypes.SingleResultResponse<cartTypes.ProductLookupResponse>,
      cartTypes.ProductByBarcodeArgs
    >({
      query: ({ barcode }) => ({
        url: "/product-by-barcode",
        method: "GET",
        params: { barcode },
      }),
    }),

    zoneLoad: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.ZoneLoadResponse>,
      cartTypes.ZoneLoadArgs
    >({
      query: ({ zone_id }) => ({
        url: `/zone/${zone_id}/load`,
        method: "POST",
      }),
    }),

    getSession: builder.query<
      cartTypes.SingleResultResponse<cartTypes.CartSessionResponse>,
      cartTypes.GetSessionArgs
    >({
      query: ({ session_id }) => ({
        url: `/${session_id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    listSessions: builder.query<
      cartTypes.MultiResultsResponse<cartTypes.CartSessionListItemResponse>,
      cartTypes.ListSessionsArgs | void
    >({
      query: (params) => ({
        url: "/sessions",
        method: "GET",
        params,
      }),
    }),

    applyEdits: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CartSessionResponse>,
      cartTypes.ApplyEditsArgs
    >({
      query: ({ session_id, body }) => ({
        url: `/${session_id}/edits`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    validateCart: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.ValidateCartResponse>,
      cartTypes.ValidateCartArgs
    >({
      query: ({ session_id, body }) => ({
        url: `/${session_id}/validate-cart`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    noPurchase: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.ValidateCartResponse>,
      cartTypes.NoPurchaseArgs
    >({
      query: ({ session_id }) => ({
        url: `/${session_id}/no-purchase`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    reviewClaim: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CartSessionResponse>,
      cartTypes.ReviewClaimArgs
    >({
      query: ({ session_id }) => ({
        url: `/${session_id}/review/claim`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    reviewResolve: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CartSessionResponse>,
      cartTypes.ReviewResolveArgs
    >({
      query: ({ session_id, body }) => ({
        url: `/${session_id}/review/resolve`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    reviewReject: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CartSessionResponse>,
      cartTypes.ReviewRejectArgs
    >({
      query: ({ session_id, transaction_id }) => ({
        url: `/${session_id}/review/reject/${transaction_id}`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    checkoutPay: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CheckoutPayResponse>,
      cartTypes.CheckoutPayRequest
    >({
      query: (data) => ({
        url: `/checkout/pay`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { session_id }) => [
        { type: "CartSession", id: session_id },
      ],
    }),

    cancel: builder.mutation<
      cartTypes.SingleResultResponse<cartTypes.CancelCartResponse>,
      cartTypes.CancelCartRequest
    >({
      query: (data) => ({
        url: `/cancel`,
        method: "POST",
        data,
      }),
      invalidatesTags: (_result, _error, { cart_session_id }) =>
        cart_session_id ? [{ type: "CartSession", id: cart_session_id }] : [],
    }),
  }),
});

export const {
  useListZonesQuery,
  useLazyListZonesQuery,
  useProductByBarcodeQuery,
  useLazyProductByBarcodeQuery,
  useZoneLoadMutation,
  useGetSessionQuery,
  useLazyGetSessionQuery,
  useListSessionsQuery,
  useLazyListSessionsQuery,
  useApplyEditsMutation,
  useValidateCartMutation,
  useNoPurchaseMutation,
  useReviewClaimMutation,
  useReviewResolveMutation,
  useReviewRejectMutation,
  useCheckoutPayMutation,
  useCancelMutation,
} = cartApi;
